'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import CenteredCarousel from '@src/components/Shared/CenteredCarousel';
import InlineSVG from '@src/components/Shared/InlineSVG';
import NextImage from '@src/components/Shared/NextImage';
import NextLink from '@src/components/Shared/NextLink';

import HeroValdProd from '@src/components/Sections/HeroValdProd';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import SubscriptionTable from '@src/components/Shared/SubscriptionTable';
import Testimonials from '@src/components/Sections/Testimonials';

import { GymAwarePageClientPropTypes } from './GymAwarePage.types';

const GymAwarePage: React.FC<GymAwarePageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/gymaware`, baseURL: '' }),
  );
  setContentType('site-page');

  // The two GymAware product shots have different aspect ratios (the Flex callouts sit further
  // apart vertically). From `md` up they share one box built from the average of the two ratios,
  // so the columns line up in height and position while both images stay close in scale.
  const twoModelsShots = page?.twoModels?.items?.[0]?.extraAssetsCollection?.items
    ?.slice(0, 2)
    ?.map((model) => model?.mediaSecondary);
  const twoModelsShotRatio =
    (twoModelsShots?.reduce((total, shot) => total + (shot?.width ?? 0) / (shot?.height || 1), 0) ?? 0) /
    (twoModelsShots?.length || 1);

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='mt-[4rem]! lg:mb-[6.5rem]! 2xl:mt-[-2rem]! [&_a]:font-[300]!'
        imgClassName='mt-[2rem] md:absolute md:right-[0] md:w-[55rem] md:bottom-[2rem] lg:relative lg:w-[60rem] lg:bottom-[-2rem] 2xl:left-[5rem] 2xl:w-7xl 3xl:left-[-10rem] xsm:min-h-[20.5797rem] md:min-h-[30rem] lg:min-h-[33rem] 2xl:min-h-[35.3rem] 3xl:min-h-[38.2484rem]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:max-w-[25.7rem] md:mt-[4.1rem] lg:max-w-[35.9302rem] 2xl:max-w-[53.96rem] xsm:min-h-[5.3219rem] md:min-h-[4.9484rem] lg:min-h-[6.9188rem] 2xl:min-h-[10.3906rem]'
        introClassName='[&>p]:tracking-[-0.0342rem] [&>p]:leading-[2.7rem] [&>p]:text-[1.8rem] [&>p]:max-w-[30.5rem] md:[&>p]:text-[2rem] md:[&>p]:leading-[3rem] md:[&>p]:tracking-[-0.036rem] md:[&>p]:max-w-[39.6rem] lg:[&>p]:max-w-[48.5rem] lg:[&>p]:text-[2.4rem] lg:[&>p]:leading-[3.6rem] 2xl:[&>p]:max-w-[58.3rem] [&_a]:text-[1.8rem] md:[&_a]:text-[2rem] lg:[&_a]:text-[2.4rem]'
        headerClassName='md:mx-[unset] md:w-[80%] md:self-start lg:self-center lg:w-[50%]'
      />

      <ImageTextSecondary
        id={page?.weightRoomQuantified?.items?.[0]?.scrollTo?.slice(1)}
        background='lg:bg-background-tertiary'
        className='overflow-visible! lg:px-[4rem] lg:py-[8rem]'
        titleClassName='md:max-w-[40rem]'
        pretitle={page?.weightRoomQuantified?.items?.[0]?.preTitle}
        title={page?.weightRoomQuantified?.items?.[0]?.title}
        imgURL={page?.weightRoomQuantified?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.weightRoomQuantified?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.weightRoomQuantified?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        className='lg:py-[0]'
        background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full! '
        id={page?.programVelocity?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.programVelocity?.items?.[0]?.preTitle}
        title={page?.programVelocity?.items?.[0]?.title}
        imgURL={page?.programVelocity?.items?.[0]?.mediaPrimary?.url}
        titleClassName='md:max-w-[80rem]'
        imgAlt={page?.programVelocity?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        imgSecondClassName='lg:w-[70%]!'
        childrenElements={
          <>
            <RenderBodyCopy
              className='max-w-[51.7rem] text-left 2xl:max-w-[78.2rem]'
              bodyCopy={page?.programVelocity?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons className='w-full' items={page?.programVelocity?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.measureAnything?.items?.[0]?.scrollTo?.slice(1)}
        className='py-[8rem] md:flex md:flex-col lg:py-[8rem]'
        background='bg-background-tertiary'
      >
        <SectionHeader
          className='justify-center lg:pb-[unset]'
          pretitle={page?.measureAnything?.items?.[0]?.preTitle}
          title={page?.measureAnything?.items?.[0]?.title}
          titleClassName='max-w-[45.6rem] text-[2.8rem] mb-[2.4rem] leading-[2.8rem] lg:locale-es:max-w-[68rem] lg:text-[4.8rem] lg:leading-[5rem] lg:locale-fr:max-w-[71rem]'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.measureAnything?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons
              className='mb-[6rem] w-full justify-center'
              items={page?.measureAnything?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>

        <CenteredCarousel list={page?.measureAnything?.items?.[0]?.extraAssetsCollection?.items} />
      </Container>

      <ImageTextSecondary
        id={page?.theMostResearched?.items?.[0]?.scrollTo?.slice(1)}
        background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full!'
        pretitle={page?.theMostResearched?.items?.[0]?.preTitle}
        title={page?.theMostResearched?.items?.[0]?.title}
        titleClassName='md:max-w-[60rem]'
        imgURL={page?.theMostResearched?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.theMostResearched?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.theMostResearched?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.theMostResearched?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.twoModels?.items?.[0]?.scrollTo?.slice(1)}
        className='pt-[4rem] pb-[2rem] md:flex md:flex-col lg:pt-[8rem]'
        background='bg-background-tertiary'
      >
        <SectionHeader
          className='text-center md:mb-[3.2rem] lg:mb-[6.4rem] lg:pb-[unset]'
          pretitle={page?.twoModels?.items?.[0]?.preTitle}
          title={page?.twoModels?.items?.[0]?.title}
          titleClassName='max-w-[34rem] text-[2.8rem] md:text-center md:max-w-[50rem] lg:max-w-[70rem] mb-[2.4rem] leading-[2.8rem] lg:text-[4.8rem] lg:leading-[5rem]  lg:locale-fr:max-w-[55rem]'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='md:text-center 2xl:max-w-[78.2rem]'
              bodyCopy={page?.twoModels?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons className='md:justify-center' items={page?.twoModels?.items?.[0]?.buttonCollection?.items} />
          </>
        </SectionHeader>

        <div className='mt-[3.2rem] flex flex-col items-center gap-[4.8rem] lg:mx-auto lg:w-full lg:max-w-[130rem] lg:flex-row lg:items-stretch lg:gap-[2.4rem] lg:[&>*]:min-w-0 lg:[&>*]:flex-1'>
          {page?.twoModels?.items?.[0]?.extraAssetsCollection?.items?.slice(0, 2)?.map((model, index) => {
            // Both portrait product shots are authored on the FIRST entry: mediaTertiary is
            // GymAware RS, mediaQuaternary is GymAware Flex. They only render below `md`.
            const mobileMedia =
              (index === 0
                ? page?.twoModels?.items?.[0]?.extraAssetsCollection?.items?.[0]?.mediaTertiary
                : page?.twoModels?.items?.[0]?.extraAssetsCollection?.items?.[0]?.mediaQuaternary) ??
              model?.mediaSecondary;

            return (
              <div key={model?.sys?.id} className='flex w-full flex-col'>
                {model?.mediaPrimary?.url && (
                  // The two logos are separate SVGs of equal height but different widths
                  // ("GYMAWARE FLEX" is a longer wordmark than "GYMAWARE RS"), so they are
                  // sized by height — a shared max-width would shrink the wider one.
                  <InlineSVG
                    className='mb-[2.4rem] flex h-[2.8rem] items-center justify-center md:h-[3.4rem] lg:h-[4rem] [&>svg]:h-auto! [&>svg]:max-h-full! [&>svg]:w-auto! [&>svg]:max-w-full!'
                    src={model?.mediaPrimary?.url}
                    renderAt={['desktop', 'tablet', 'mobile']}
                  />
                )}

                <RenderBodyCopy
                  className='mx-auto mb-[3.2rem] max-w-[42.7rem] grow text-center'
                  bodyCopy={model?.bodyCopy?.json?.content}
                />
                <NextImage
                  className='mx-auto w-full max-w-[38rem] md:hidden'
                  objectFit='contain'
                  entryId={model?.sys?.id}
                  fieldId={index === 0 ? 'mediaTertiary' : 'mediaQuaternary'}
                  src={mobileMedia?.url}
                  alt={mobileMedia?.description}
                  aspectRatio={mobileMedia?.width / mobileMedia?.height}
                  width={mobileMedia?.width}
                />
                <NextImage
                  // The Flex unit sits higher inside its canvas than the RS unit, so the two
                  // devices read as misaligned. The copy grows to bottom-align both images, which
                  // means this margin under the RS shot is what drops the Flex one to match it.
                  className={`mx-auto hidden w-full md:block md:min-h-[0] md:max-w-[52rem] lg:max-w-none ${
                    index === 0 ? 'md:mb-[2.8rem] lg:mb-[3.6rem]' : ''
                  }`}
                  style={{ aspectRatio: twoModelsShotRatio }}
                  imgClassName='md:h-full! md:w-full! md:object-contain!'
                  objectFit='contain'
                  entryId={model?.sys?.id}
                  fieldId='mediaSecondary'
                  src={model?.mediaSecondary?.url}
                  alt={model?.mediaSecondary?.description}
                  aspectRatio={model?.mediaSecondary?.width / model?.mediaSecondary?.height}
                  width={model?.mediaSecondary?.width}
                />
              </div>
            );
          })}
        </div>
      </Container>

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
        className='[&>div:last-child]:bg-[white]'
      />

      <FeaturedProducts
        data={page?.velocityBasedTrainingFurther?.items?.[0]}
        className='lg:locale-fr:[&_h2]:max-w-[86rem] py-[4rem] lg:py-[8rem]'
        background='bg-[#F9F9F9]'
      />

      <Container
        id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[white]'
        className='mt-[0]! py-[8rem]'
      >
        <SectionHeader
          className='mb-[2.4rem] lg:mb-[unset]'
          childrenClassName='w-full'
          pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
          title={page?.valdSubscription?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
          align='center'
        >
          <RenderButtons
            items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
            className='mt-[1rem]! justify-center'
          />
        </SectionHeader>
        <SubscriptionTable
          headerImgL='w-[11rem] lg:w-[12.4rem] [&>img]:max-h-[11rem] [&>img]:object-contain lg:[&>img]:max-h-[12.4rem]'
          headerImgR='w-[11rem] lg:w-[13.5rem] [&>img]:max-h-[11rem] [&>img]:object-contain lg:[&>img]:max-h-[12.4rem]'
          data={page?.valdSubscription?.items}
        />
      </Container>

      <FeaturedProducts
        data={page?.valdSystems?.items?.[0]}
        className='lg:locale-fr:[&_h2]:max-w-[64rem] py-[4rem] lg:py-[8rem]'
        background='bg-[#F9F9F9]'
      />

      <CallToAction
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default GymAwarePage;
